/**
 * useNotifications Hook
 *
 * Provides a clean React hook interface for managing notifications with real-time subscriptions,
 * caching, and state management.
 *
 * Features:
 * - Real-time subscriptions to notifications and unread count
 * - Global caching to prevent duplicate API calls
 * - Optimistic updates for better UX
 * - Comprehensive error handling and loading states
 * - Integration with NotificationService and user context
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuthUnified } from './useAuthUnified';
import { NotificationService, type NotificationDocument } from '@/services/shared/notificationService';
import type { Unsubscribe } from 'firebase/firestore';

// Global cache to prevent multiple hook instances from making the same API calls
const notificationsCache = new Map<string, { data: NotificationDocument[]; timestamp: number }>();
const unreadCountCache = new Map<string, { data: number; timestamp: number }>();
const loadingCache = new Set<string>();

// Cache duration (5 minutes, same as NotificationService)
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Performance optimization utilities
 */
const performanceUtils = {
  /**
   * Check if cached data is still valid
   */
  isCacheValid: (timestamp: number): boolean => {
    return Date.now() - timestamp < CACHE_DURATION;
  },

  /**
   * Clean up expired cache entries
   */
  cleanupExpiredCache: (): void => {
    const now = Date.now();
    
    // Clean notifications cache
    for (const [key, entry] of notificationsCache.entries()) {
      if (now - entry.timestamp >= CACHE_DURATION) {
        notificationsCache.delete(key);
      }
    }
    
    // Clean unread count cache
    for (const [key, entry] of unreadCountCache.entries()) {
      if (now - entry.timestamp >= CACHE_DURATION) {
        unreadCountCache.delete(key);
      }
    }
  },

  /**
   * Debounce function for performance optimization
   */
  debounce: <T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }
};

// Clean up expired cache entries every 5 minutes
setInterval(performanceUtils.cleanupExpiredCache, CACHE_DURATION);

interface UseNotificationsReturn {
  // State
  notifications: NotificationDocument[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismissNotification: (notificationId: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  
  // Loading states for individual actions
  isMarkingAsRead: boolean;
  isMarkingAllAsRead: boolean;
  isDismissing: boolean;
}

/**
 * Hook for managing user notifications with real-time updates
 * 
 * Provides comprehensive notification management following established patterns:
 * - Real-time subscriptions with automatic cleanup
 * - Global caching for performance optimization
 * - Individual loading states for better UX
 * - Error handling with user-friendly messages
 * 
 * @returns Notification management interface
 */
export const useNotifications = (): UseNotificationsReturn => {
  const { effectiveUser } = useAuthUnified();
  
  // State management
  const [notifications, setNotifications] = useState<NotificationDocument[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Individual action loading states
  const [isMarkingAsRead, setIsMarkingAsRead] = useState<boolean>(false);
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState<boolean>(false);
  const [isDismissing, setIsDismissing] = useState<boolean>(false);
  
  // Refs for subscription management and performance optimization
  const subscriptionsRef = useRef<{
    notifications?: Unsubscribe;
    unreadCount?: Unsubscribe;
  }>({});
  const mountedRef = useRef(true);
  const lastUpdateRef = useRef<number>(0);
  
  // Get current user ID for subscriptions - memoized for performance
  const userId = useMemo(() => {
    return effectiveUser?.employee_id?.toString() || null;
  }, [effectiveUser?.employee_id]);
  
  // Cache key for this user - memoized for performance
  const cacheKey = useMemo(() => {
    return userId ? `notifications-${userId}` : null;
  }, [userId]);
  
  // Cleanup function for unmounting
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  /**
   * Load initial notifications with caching
   */
  const loadInitialNotifications = useCallback(async () => {
    if (!userId || !cacheKey) return;
    
    // Check global cache first
    const cachedEntry = notificationsCache.get(cacheKey);
    if (cachedEntry && performanceUtils.isCacheValid(cachedEntry.timestamp)) {
      console.log('[USE-NOTIFICATIONS] Using cached notifications for user:', userId);
      setNotifications(cachedEntry.data);
      return;
    }
    
    // Skip if already loading
    if (loadingCache.has(cacheKey)) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      loadingCache.add(cacheKey);
      
      console.log('[USE-NOTIFICATIONS] Loading initial notifications for user:', userId);
      
      const initialNotifications = await NotificationService.getUserNotifications(userId);
      
      // Cache the result globally with timestamp
      notificationsCache.set(cacheKey, {
        data: initialNotifications,
        timestamp: Date.now()
      });
      setNotifications(initialNotifications);
      
      console.log(`[USE-NOTIFICATIONS] Loaded ${initialNotifications.length} notifications for user:`, userId);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load notifications';
      console.error('[USE-NOTIFICATIONS] Error loading notifications:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      loadingCache.delete(cacheKey);
    }
  }, [userId, cacheKey]);
  
  /**
   * Set up real-time subscriptions with improved performance and cleanup
   */
  useEffect(() => {
    if (!userId) {
      // Clean up existing subscriptions if user changes
      if (subscriptionsRef.current.notifications) {
        subscriptionsRef.current.notifications();
        subscriptionsRef.current.notifications = undefined;
      }
      if (subscriptionsRef.current.unreadCount) {
        subscriptionsRef.current.unreadCount();
        subscriptionsRef.current.unreadCount = undefined;
      }
      
      // Reset state
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
      return;
    }
    
    console.log('[USE-NOTIFICATIONS] Setting up real-time subscriptions for user:', userId);
    
    // Load initial data
    loadInitialNotifications();
    
    // Set up notifications subscription with throttling
    const notificationsUnsub = NotificationService.subscribeToUserNotifications(
      userId,
      performanceUtils.debounce((updatedNotifications) => {
        if (!mountedRef.current) return;
        
        const now = Date.now();
        // Throttle updates to prevent excessive re-renders
        if (now - lastUpdateRef.current < 100) return;
        lastUpdateRef.current = now;
        
        console.log(`[USE-NOTIFICATIONS] Real-time notifications update: ${updatedNotifications.length} notifications`);
        
        // Update cache with timestamp
        if (cacheKey) {
          notificationsCache.set(cacheKey, {
            data: updatedNotifications,
            timestamp: now
          });
        }
        
        setNotifications(updatedNotifications);
        setError(null); // Clear any previous errors on successful update
      }, 100)
    );
    
    // Set up unread count subscription with throttling
    const unreadCountUnsub = NotificationService.subscribeToUnreadCount(
      userId,
      performanceUtils.debounce((count) => {
        if (!mountedRef.current) return;
        
        console.log(`[USE-NOTIFICATIONS] Real-time unread count update: ${count}`);
        
        // Update cache with timestamp
        if (cacheKey) {
          unreadCountCache.set(cacheKey, {
            data: count,
            timestamp: Date.now()
          });
        }
        
        setUnreadCount(count);
      }, 100)
    );
    
    // Store unsubscribe functions in ref for cleanup
    subscriptionsRef.current.notifications = notificationsUnsub;
    subscriptionsRef.current.unreadCount = unreadCountUnsub;
    
    // Cleanup function
    return () => {
      console.log('[USE-NOTIFICATIONS] Cleaning up subscriptions for user:', userId);
      if (subscriptionsRef.current.notifications) {
        subscriptionsRef.current.notifications();
        subscriptionsRef.current.notifications = undefined;
      }
      if (subscriptionsRef.current.unreadCount) {
        subscriptionsRef.current.unreadCount();
        subscriptionsRef.current.unreadCount = undefined;
      }
    };
  }, [userId, cacheKey, loadInitialNotifications]);
  
  /**
   * Mark a single notification as read with optimistic updates
   * Memoized with proper dependencies for performance
   */
  const markAsRead = useCallback(async (notificationId: string): Promise<void> => {
    if (!notificationId || isMarkingAsRead || !mountedRef.current) return;
    
    try {
      setIsMarkingAsRead(true);
      setError(null);
      
      console.log('[USE-NOTIFICATIONS] Marking notification as read:', notificationId);
      
      // Optimistic update - immediately update local state
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, status: 'read' as const }
            : notification
        )
      );
      
      // Update unread count optimistically
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Make API call
      await NotificationService.markAsRead(notificationId);
      
      if (mountedRef.current) {
        console.log('[USE-NOTIFICATIONS] Successfully marked notification as read:', notificationId);
      }
      
    } catch (err) {
      if (mountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to mark notification as read';
        console.error('[USE-NOTIFICATIONS] Error marking notification as read:', err);
        setError(errorMessage);
      }
      
      // Revert optimistic update on error
      // Real-time subscription will restore correct state
      
    } finally {
      if (mountedRef.current) {
        setIsMarkingAsRead(false);
      }
    }
  }, [isMarkingAsRead]);
  
  /**
   * Mark all notifications as read
   * Memoized with proper dependencies for performance
   */
  const markAllAsRead = useCallback(async (): Promise<void> => {
    if (!userId || isMarkingAllAsRead || !mountedRef.current) return;
    
    try {
      setIsMarkingAllAsRead(true);
      setError(null);
      
      console.log('[USE-NOTIFICATIONS] Marking all notifications as read for user:', userId);
      
      // Optimistic update - mark all as read
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, status: 'read' as const }))
      );
      setUnreadCount(0);
      
      // Make API call
      await NotificationService.markAllAsRead(userId);
      
      if (mountedRef.current) {
        console.log('[USE-NOTIFICATIONS] Successfully marked all notifications as read for user:', userId);
      }
      
    } catch (err) {
      if (mountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to mark all notifications as read';
        console.error('[USE-NOTIFICATIONS] Error marking all notifications as read:', err);
        setError(errorMessage);
      }
      
      // Real-time subscription will restore correct state on error
      
    } finally {
      if (mountedRef.current) {
        setIsMarkingAllAsRead(false);
      }
    }
  }, [userId, isMarkingAllAsRead]);
  
  /**
   * Dismiss/delete a notification with optimistic updates
   * Memoized with proper dependencies for performance
   */
  const dismissNotification = useCallback(async (notificationId: string): Promise<void> => {
    if (!notificationId || isDismissing || !mountedRef.current) return;
    
    try {
      setIsDismissing(true);
      setError(null);
      
      console.log('[USE-NOTIFICATIONS] Dismissing notification:', notificationId);
      
      // Find the notification to check if it was unread
      const notification = notifications.find(n => n.id === notificationId);
      const wasUnread = notification?.status === 'unread';
      
      // Optimistic update - remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Update unread count if it was unread
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      // Make API call
      await NotificationService.dismissNotification(notificationId);
      
      if (mountedRef.current) {
        console.log('[USE-NOTIFICATIONS] Successfully dismissed notification:', notificationId);
      }
      
    } catch (err) {
      if (mountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to dismiss notification';
        console.error('[USE-NOTIFICATIONS] Error dismissing notification:', err);
        setError(errorMessage);
      }
      
      // Real-time subscription will restore correct state on error
      
    } finally {
      if (mountedRef.current) {
        setIsDismissing(false);
      }
    }
  }, [notifications, isDismissing]);
  
  /**
   * Force refresh notifications (bypass cache)
   */
  const refreshNotifications = useCallback(async (): Promise<void> => {
    if (!userId || !cacheKey) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[USE-NOTIFICATIONS] Force refreshing notifications for user:', userId);
      
      // Clear cache
      notificationsCache.delete(cacheKey);
      unreadCountCache.delete(cacheKey);
      
      // Fetch fresh data
      const freshNotifications = await NotificationService.getUserNotifications(userId);
      const freshUnreadCount = await NotificationService.getUnreadCount(userId);
      
      // Update cache and state with timestamps
      notificationsCache.set(cacheKey, {
        data: freshNotifications,
        timestamp: Date.now()
      });
      unreadCountCache.set(cacheKey, {
        data: freshUnreadCount,
        timestamp: Date.now()
      });
      
      setNotifications(freshNotifications);
      setUnreadCount(freshUnreadCount);
      
      console.log(`[USE-NOTIFICATIONS] Refreshed ${freshNotifications.length} notifications, ${freshUnreadCount} unread`);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh notifications';
      console.error('[USE-NOTIFICATIONS] Error refreshing notifications:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [userId, cacheKey]);
  
  // Return the hook interface
  return {
    // State
    notifications,
    unreadCount,
    isLoading,
    error,
    
    // Actions
    markAsRead,
    markAllAsRead,
    dismissNotification,
    refreshNotifications,
    
    // Loading states for individual actions
    isMarkingAsRead,
    isMarkingAllAsRead,
    isDismissing,
  };
};

